import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Info } from 'lucide-react';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useAuth } from '@/auth/AuthProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { SkeletonLines } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { toast } from '@/components/ui/toaster';
import { AdminNote, SettingsCard, SettingsSection } from '@/pages/settings/SettingsSection';
import { unwrapList } from '@/pages/settings/useSettings';
import { TEMPLATE_BLOCKS, TEMPLATE_VARIABLES } from '@/pages/settings/template-variables';

export interface MessageTemplateRow {
  key: string;
  name: string;
  description: string | null;
  subjectTemplate: string;
  bodyTemplate: string;
  allowsIntermediaryName: boolean;
  updatedAt?: string;
  updatedBy?: string | null;
}

const TEMPLATES_KEY = ['message-templates'] as const;

/** Message templates (spec §9): list on the left, editor with the placeholder reference on the right. */
export function TemplatesSettings() {
  const { isAdmin } = useAuth();
  const templates = useQuery({
    queryKey: TEMPLATES_KEY,
    queryFn: async () => unwrapList<MessageTemplateRow>(await api.get<unknown>('/message-templates')),
  });
  const [selectedKey, setSelectedKey] = React.useState<string | null>(null);
  const list = templates.data ?? [];
  const selected = list.find((t) => t.key === selectedKey) ?? list[0] ?? null;

  return (
    <SettingsSection
      title="Message templates"
      description="Drafts for e-mails to intermediaries and suppliers. Nothing is sent from ClawHire; the text is copied."
    >
      {isAdmin ? null : <AdminNote what="message templates" />}
      {templates.isLoading ? (
        <SkeletonLines lines={6} />
      ) : templates.isError ? (
        <p role="alert" className="text-sm text-destructive">
          Templates could not be loaded: {templates.error.message}
        </p>
      ) : list.length === 0 ? (
        <EmptyState
          compact
          title="No templates"
          description="The API seeds the default templates on first start."
        />
      ) : (
        <div className="grid gap-6 lg:grid-cols-[14rem_minmax(0,1fr)]">
          <nav aria-label="Templates">
            <ul className="flex gap-1 overflow-x-auto lg:flex-col">
              {list.map((t) => {
                const active = selected?.key === t.key;
                return (
                  <li key={t.key} className="shrink-0">
                    <button
                      type="button"
                      aria-current={active ? 'true' : undefined}
                      onClick={() => setSelectedKey(t.key)}
                      className={cn(
                        'w-full rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        active ? 'bg-accent font-medium text-accent-foreground' : 'text-muted-foreground',
                      )}
                    >
                      {t.name}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
          {selected ? <TemplateEditor key={selected.key} template={selected} readOnly={!isAdmin} /> : null}
        </div>
      )}
    </SettingsSection>
  );
}

function TemplateEditor({ template, readOnly }: { template: MessageTemplateRow; readOnly: boolean }) {
  const queryClient = useQueryClient();
  const [name, setName] = React.useState(template.name);
  const [description, setDescription] = React.useState(template.description ?? '');
  const [subject, setSubject] = React.useState(template.subjectTemplate);
  const [body, setBody] = React.useState(template.bodyTemplate);

  const dirty =
    name !== template.name ||
    description !== (template.description ?? '') ||
    subject !== template.subjectTemplate ||
    body !== template.bodyTemplate;

  const save = useMutation({
    mutationFn: () => {
      const patch: Partial<
        Pick<MessageTemplateRow, 'name' | 'description' | 'subjectTemplate' | 'bodyTemplate'>
      > = {};
      if (name !== template.name) patch.name = name.trim();
      if (description !== (template.description ?? '')) patch.description = description.trim() || null;
      if (subject !== template.subjectTemplate) patch.subjectTemplate = subject;
      if (body !== template.bodyTemplate) patch.bodyTemplate = body;
      return api.patch<MessageTemplateRow>(`/message-templates/${encodeURIComponent(template.key)}`, patch);
    },
    onSuccess: async (saved) => {
      queryClient.setQueryData<MessageTemplateRow[]>(TEMPLATES_KEY, (old) =>
        old ? old.map((t) => (t.key === saved.key ? { ...t, ...saved } : t)) : old,
      );
      await queryClient.invalidateQueries({ queryKey: TEMPLATES_KEY });
      toast.success(`Template "${saved.name}" saved`);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const reset = () => {
    setName(template.name);
    setDescription(template.description ?? '');
    setSubject(template.subjectTemplate);
    setBody(template.bodyTemplate);
  };

  const ids = {
    name: `tpl-${template.key}-name`,
    description: `tpl-${template.key}-description`,
    subject: `tpl-${template.key}-subject`,
    body: `tpl-${template.key}-body`,
  };

  return (
    <div className="min-w-0 space-y-4">
      <SettingsCard>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (dirty && !readOnly) save.mutate();
          }}
        >
          <div className="flex flex-wrap items-center gap-2">
            <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{template.key}</code>
            {template.allowsIntermediaryName ? (
              <Badge variant="secondary" className="font-normal">
                may name the intermediary
              </Badge>
            ) : (
              <Badge variant="muted" className="font-normal">
                checked against forbidden terms
              </Badge>
            )}
            {template.updatedAt ? (
              <span className="text-xs text-muted-foreground">
                Updated {formatDateTime(template.updatedAt)}
                {template.updatedBy ? ` · ${template.updatedBy}` : ''}
              </span>
            ) : null}
          </div>
          <fieldset disabled={readOnly} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor={ids.name}>Name</Label>
                <Input id={ids.name} value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
              </div>
              <div className="space-y-2">
                <Label htmlFor={ids.description}>Description</Label>
                <Input
                  id={ids.description}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={1000}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor={ids.subject}>Subject</Label>
              <Textarea
                id={ids.subject}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                rows={2}
                className="min-h-0 font-mono text-xs"
                spellCheck={false}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={ids.body}>Body</Label>
              <Textarea
                id={ids.body}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={16}
                className="font-mono text-xs"
                spellCheck={false}
              />
            </div>
          </fieldset>
          {readOnly ? null : (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="submit"
                loading={save.isPending}
                disabled={!dirty || !name.trim() || !subject.trim() || !body.trim()}
              >
                Save template
              </Button>
              <Button type="button" variant="ghost" onClick={reset} disabled={!dirty}>
                Discard
              </Button>
              {dirty ? <span className="text-xs text-muted-foreground">Unsaved changes</span> : null}
            </div>
          )}
        </form>
      </SettingsCard>

      <VariablesHelp />
    </div>
  );
}

function VariablesHelp() {
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Copied ${text}`);
    } catch {
      toast.error('Could not copy');
    }
  };
  return (
    <details className="group rounded-lg border bg-card text-card-foreground shadow-sm">
      <summary className="flex cursor-pointer items-center gap-2 px-5 py-3 text-sm font-semibold marker:content-none">
        <Info className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        Available placeholders
        <span className="ml-auto text-xs font-normal text-muted-foreground group-open:hidden">show</span>
        <span className="ml-auto hidden text-xs font-normal text-muted-foreground group-open:inline">
          hide
        </span>
      </summary>
      <div className="space-y-4 border-t px-5 py-4 text-sm">
        <p className="text-muted-foreground">
          Write placeholders as <code>{'{{name}}'}</code>. Missing values render empty. Click a placeholder to
          copy it.
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          {TEMPLATE_VARIABLES.map((g) => (
            <div key={g.group}>
              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {g.group}
              </h4>
              <ul className="space-y-0.5">
                {g.variables.map((v) => (
                  <li key={v.name} className="flex items-start gap-2">
                    <button
                      type="button"
                      className="inline-flex shrink-0 items-center gap-1 rounded bg-muted px-1.5 py-0.5 font-mono text-xs hover:bg-accent"
                      onClick={() => void copy(`{{${v.name}}}`)}
                      aria-label={`Copy {{${v.name}}}`}
                    >
                      {`{{${v.name}}}`}
                      <Copy className="h-3 w-3 opacity-50" aria-hidden="true" />
                    </button>
                    <span className="text-xs text-muted-foreground">{v.description}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div>
          <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Blocks</h4>
          <ul className="space-y-1">
            {TEMPLATE_BLOCKS.map((b) => (
              <li key={b.syntax} className="flex flex-wrap items-start gap-2">
                <code className="whitespace-pre rounded bg-muted px-1.5 py-0.5 text-xs">{b.syntax}</code>
                <span className="text-xs text-muted-foreground">{b.description}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </details>
  );
}
